import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "../housestyle.css";
import "./Giveaways.css";
import api from "../API/Api";
import countdown_tick from "./countdown_tick.mp3";
import ping_tick from "./ping.mp3";
import winner_sfx from "./winner.mp3";
// Note: countdown_tick, ping_tick, winner_sfx are used via module-level Audio objects below

// Create at module scope so the prop named "winner" inside SingleSpin doesn't shadow them
const pingAudio = new Audio(ping_tick);
pingAudio.volume = 0.3;
const winnerAudio = new Audio(winner_sfx);
const countdownAudio = new Audio(countdown_tick);

// Mute state persisted across sessions — off by default
let sfxMuted = localStorage.getItem("gw_sfx_muted") !== "0";
function setGlobalMute(muted) {
  sfxMuted = muted;
  try { localStorage.setItem("gw_sfx_muted", muted ? "1" : "0"); } catch { }
}
function playAudio(audio) {
  if (sfxMuted) return;
  audio.currentTime = 0;
  audio.play().catch(() => { });
}

// Browsers block audio until the user interacts. Unlock all audio on first interaction.
function unlockAudio() {
  if (sfxMuted) return;
  [pingAudio, winnerAudio, countdownAudio].forEach((a) => {
    const vol = a.volume;
    a.volume = 0;
    a.play().then(() => { a.pause(); a.currentTime = 0; a.volume = vol; }).catch(() => { });
  });
  document.removeEventListener("pointerdown", unlockAudio);
}
document.addEventListener("pointerdown", unlockAudio);

// Works for both discord and playtime entries/winners
function entryName(e) {
  return e.player_name ?? e.discord_tag ?? "";
}
function entryTickets(e) {
  return e.tickets ?? e.invites ?? 1;
}

const video =
  "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_2.mp4";

function spinKey(g) {
  return `gw_watched_${g.id}_${g.end_date}`;
}
function hasWatchedSpin(g) {
  try {
    return localStorage.getItem(spinKey(g)) === "1";
  } catch {
    return false;
  }
}
function markSpinWatched(g) {
  try {
    localStorage.setItem(spinKey(g), "1");
  } catch { }
}

function useCountdown(endDate) {
  const [timeLeft, setTimeLeft] = useState(null);
  useEffect(() => {
    if (!endDate) return;
    const tick = () => {
      const diff = new Date(endDate) - Date.now();
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [endDate]);
  return timeLeft;
}

/* Confetti burst rendered on winner reveal — pure CSS pieces */
function Confetti({ count = 48 }) {
  const pieces = useMemo(() => {
    const colors = ["#f0ab20", "#e84c3d", "#80d898", "#6ec3f4", "#e8e8e8", "#d4920a"];
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.4 + Math.random() * 1.8,
      size: 5 + Math.random() * 6,
      color: colors[i % colors.length],
      spin: (Math.random() > 0.5 ? 1 : -1) * (420 + Math.random() * 480),
      drift: -40 + Math.random() * 80,
      round: Math.random() > 0.7,
    }));
  }, [count]);

  return (
    <div className="gw-confetti" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.round ? p.size : p.size * 0.5,
            background: p.color,
            borderRadius: p.round ? "50%" : "1px",
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--spin": `${p.spin}deg`,
            "--drift": `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

function PreSpinCountdown({ onDone, seconds = 10 }) {
  const [count, setCount] = useState(seconds);
  useEffect(() => {
    if (count <= 0) {
      onDone();
      return;
    }
    playAudio(countdownAudio);
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count, onDone]);

  return (
    <div className="prespin-wrap">
      <div className="prespin-ring" key={count}>
        <div className="prespin-count">{count}</div>
      </div>
      <div className="prespin-label">Spin starts in</div>
      <div className="prespin-sub">Get ready...</div>
    </div>
  );
}

/* Horizontal case-opening style reel: heads scroll past a center marker
   and decelerate until the winner lands under it. */
const REEL_CELL = 96; // cell width (88px) + gap (8px) — keep in sync with CSS
const REEL_WINNER_IDX = 44;
const REEL_TAIL = 6;

function SingleSpin({ winner, allDiscordTags, onDone, prizeLabel }) {
  const [phase, setPhase] = useState("spinning");
  const frameRef = useRef(null);
  const stripRef = useRef(null);
  // Keep the latest onDone without retriggering the animation effect
  // (its identity changes on every data poll)
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const winnerName = entryName(winner);

  // Build the tape once — random fillers, winner pinned at REEL_WINNER_IDX
  const tape = useMemo(() => {
    const pool = allDiscordTags.length > 0 ? allDiscordTags : [winnerName];
    const others = pool.filter((n) => n !== winnerName);
    const filler = others.length > 0 ? others : pool;
    const rand = () => filler[Math.floor(Math.random() * filler.length)];
    const t = Array.from({ length: REEL_WINNER_IDX + REEL_TAIL + 1 }, rand);
    t[REEL_WINNER_IDX] = winnerName;
    return t;
  }, []); // eslint-disable-line

  useEffect(() => {
    const frame = frameRef.current;
    const strip = stripRef.current;
    if (!frame || !strip) return;

    const frameW = frame.clientWidth;
    // Land somewhere inside the winner cell, not always dead-center
    const jitter = (Math.random() - 0.5) * REEL_CELL * 0.5;
    const target =
      REEL_WINNER_IDX * REEL_CELL + REEL_CELL / 2 - frameW / 2 + jitter;
    const DURATION = 6400;
    const easeOut = (t) => 1 - Math.pow(1 - t, 5);

    let raf;
    let lastIdx = -1;
    const start = performance.now();

    const step = (now) => {
      const p = Math.min(1, (now - start) / DURATION);
      const x = easeOut(p) * target;
      strip.style.transform = `translateX(${-x}px)`;
      // Ping every time a new head crosses the marker
      const idx = Math.floor((x + frameW / 2) / REEL_CELL);
      if (idx !== lastIdx) {
        lastIdx = idx;
        playAudio(pingAudio);
      }
      if (p < 1) {
        raf = requestAnimationFrame(step);
      } else {
        setPhase("landed");
        setTimeout(() => {
          setPhase("revealed");
          playAudio(winnerAudio);
          setTimeout(() => onDoneRef.current(), 3500);
        }, 1600);
      }
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [tape]);

  return (
    <div className="spin-wrap">
      {phase === "revealed" && <Confetti />}
      <div className="spin-prize-badge">{prizeLabel}</div>
      <div className="spin-title">
        {phase === "spinning"
          ? "Spinning..."
          : phase === "landed"
            ? "We have a winner!"
            : "Winner!"}
      </div>
      {phase === "revealed" ? (
        <div className="spin-winner-stage">
          <div className="spin-winner-rays" aria-hidden="true" />
          <div className="spin-winner-card">
            <img
              src={`https://mc-heads.net/avatar/${winnerName}/96`}
              alt={winnerName}
              className="spin-winner-avatar"
            />
            <div className="spin-winner-name">{winnerName}</div>
            <div className="spin-winner-prize">{winner.prize}</div>
          </div>
        </div>
      ) : (
        <div className={`spin-track${phase === "landed" ? " spin-track--landed" : ""}`} ref={frameRef}>
          <div className="spin-track-marker" aria-hidden="true" />
          <div className="spin-strip" ref={stripRef}>
            {tape.map((name, i) => (
              <div
                key={i}
                className={`spin-cell${i === REEL_WINNER_IDX && phase === "landed" ? " spin-cell--winner" : ""}`}
              >
                <img
                  src={`https://mc-heads.net/avatar/${name}/64`}
                  alt={name}
                  loading="eager"
                />
                <span>{name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SpinSequence({ winners, allDiscordTags, giveaway, onReplay }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [done, setDone] = useState(false);

  const handleDone = useCallback(() => {
    if (currentIdx + 1 < winners.length) {
      setCurrentIdx((i) => i + 1);
    } else {
      markSpinWatched(giveaway);
      setDone(true);
    }
  }, [currentIdx, winners.length, giveaway]);

  const placeLabels = ["🥇 1st Place", "🥈 2nd Place", "🥉 3rd Place"];

  if (done) {
    return <WinnersSummary winners={winners} celebratory onReplay={onReplay} />;
  }

  return (
    <SingleSpin
      key={currentIdx}
      winner={winners[currentIdx]}
      allDiscordTags={allDiscordTags}
      onDone={handleDone}
      prizeLabel={placeLabels[currentIdx] ?? `#${currentIdx + 1}`}
    />
  );
}

function SpinSection({ winners, allDiscordTags, giveaway, preSpinSeconds = 10, onReplay }) {
  const [preSpinDone, setPreSpinDone] = useState(false);
  const [muted, setMuted] = useState(sfxMuted);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setGlobalMute(next);
  };

  return (
    <div className="spin-section-wrap">
      <button className="gw-audio-btn" onClick={toggleMute}>
        <span className={`gw-audio-dot${muted ? " gw-audio-dot--off" : ""}`} />
        Audio {muted ? "Off" : "On"}
      </button>
      {!preSpinDone
        ? <PreSpinCountdown seconds={preSpinSeconds} onDone={() => setPreSpinDone(true)} />
        : <SpinSequence winners={winners} allDiscordTags={allDiscordTags} giveaway={giveaway} onReplay={onReplay} />
      }
    </div>
  );
}

const MEDALS = ["🥇", "🥈", "🥉"];

function WinnersSummary({ winners, celebratory = false, onReplay }) {
  return (
    <div className="spin-wrap">
      {celebratory && <Confetti count={36} />}
      <div className="spin-title">{celebratory ? "All Winners!" : "Winners"}</div>
      <div className="spin-all-winners">
        {winners.map((w, i) => (
          <div
            key={i}
            className={`spin-winner-card spin-winner-card--row gw-place-${i + 1}`}
            style={{ animationDelay: `${i * 0.12}s` }}
          >
            <span className="spin-winner-medal">{MEDALS[i] ?? `#${i + 1}`}</span>
            <img
              src={`https://mc-heads.net/avatar/${entryName(w)}/64`}
              alt={entryName(w)}
              className="spin-winner-avatar spin-winner-avatar--sm"
            />
            <div>
              <div className="spin-winner-name">{entryName(w)}</div>
              <div className="spin-winner-prize">{w.prize}</div>
            </div>
          </div>
        ))}
      </div>
      {onReplay && (
        <button className="gw-replay-btn" onClick={onReplay}>
          <span className="gw-replay-icon">↻</span>
          Watch spin again
        </button>
      )}
    </div>
  );
}

const PLACE_STYLE = {
  1: { label: "🥇 1st", color: "#f0ab20" },
  2: { label: "🥈 2nd", color: "#b0b8c8" },
  3: { label: "🥉 3rd", color: "#c87840" },
};

function EntriesModal({ title, entries, isPlaytime, onClose }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalTickets = entries.reduce((s, e) => s + Math.max(1, entryTickets(e)), 0);

  return (
    <div
      className="gw-modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="gw-modal-box">
        <div className="gw-modal-header">
          <div>
            <div className="gw-modal-title">{title}</div>
            <div className="gw-modal-sub">
              {entries.length} participants · {totalTickets} total tickets
            </div>
          </div>
          <button className="gw-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="gw-modal-table-head">
          <span>Player</span>
          <span>Past wins</span>
          <span>{isPlaytime ? "Minutes" : "Invites"}</span>
          <span>Win chance</span>
        </div>

        <div className="gw-modal-rows">
          {entries.length === 0 && (
            <div className="gw-modal-empty">No entries yet.</div>
          )}
          {entries.map((player, i) => {
            const tickets = Math.max(1, entryTickets(player));
            const chance =
              totalTickets > 0
                ? ((tickets / totalTickets) * 100).toFixed(2)
                : "0.00";
            const place = PLACE_STYLE[player.place];
            const name = entryName(player);
            return (
              <div
                key={i}
                className={`gw-modal-row${player.is_winner ? " gw-modal-row--winner" : ""}`}
                style={place ? { borderLeft: `3px solid ${place.color}` } : {}}
              >
                <div className="gw-modal-player">
                  <img
                    src={`https://mc-heads.net/avatar/${name}/32`}
                    alt={name}
                    className="gw-modal-avatar"
                  />
                  <div className="gw-modal-name-wrap">
                    <span
                      className="gw-modal-name"
                      style={place ? { color: place.color } : {}}
                    >
                      {name}
                    </span>
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                      {place && (
                        <span
                          className="gw-modal-place-badge"
                          style={{ color: place.color }}
                        >
                          {place.label}
                        </span>
                      )}
                      {player.double_chance && (
                        <span className="gw-modal-egg-badge">🥚 2× Chance</span>
                      )}
                    </div>
                  </div>
                </div>
                <span className="gw-modal-wins">
                  {player.won_giveaways > 0 ? (
                    <span className="gw-modal-wins-badge">
                      {player.won_giveaways} Wins
                    </span>
                  ) : (
                    <span className="gw-modal-wins-none">0</span>
                  )}
                </span>
                <span className="gw-modal-invites">
                  {isPlaytime ? (player.playtime_minutes ?? tickets) : entryTickets(player)}
                </span>
                <div className="gw-modal-chance-wrap">
                  <span className="gw-modal-chance">{chance}%</span>
                  <div className="gw-modal-chance-bar">
                    <div
                      className="gw-modal-chance-fill"
                      style={{ width: `${Math.min(100, chance)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HowToEnter({ giveaway }) {
  if (giveaway.type === "playtime") {
    return (
      <div className="gw-howto">
        <div className="gw-howto-label">How to enter</div>
        <div className="gw-howto-steps">
          <div className="gw-howto-step">
            <div className="gw-howto-num">1</div>
            <div className="gw-howto-body">
              <span className="gw-howto-title">Join the server</span>
              <span className="gw-howto-desc">Connect to <strong>play.simplemcnetwork.net</strong></span>
            </div>
          </div>
          <div className="gw-howto-step">
            <div className="gw-howto-num">2</div>
            <div className="gw-howto-body">
              <span className="gw-howto-title">Play</span>
              {giveaway.min_playtime_seconds != null && (
                <span className="gw-howto-desc">Reach at least <strong>{Math.round(giveaway.min_playtime_seconds / 60)} min</strong> of playtime to qualify</span>
              )}
            </div>
          </div>
          <div className="gw-howto-step">
            <div className="gw-howto-num">3</div>
            <div className="gw-howto-body">
              <span className="gw-howto-title">Equal chance for everyone</span>
              <span className="gw-howto-desc">Every qualifying player gets 1 ticket</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gw-howto">
      <div className="gw-howto-label">How to enter</div>
      <div className="gw-howto-steps">
        <div className="gw-howto-step">
          <div className="gw-howto-num">1</div>
          <div className="gw-howto-body">
            <a className="gw-howto-title gw-howto-link" href="https://discord.com/invite/simplemcnetwork" target="_blank" rel="noreferrer">Join Our Discord</a>
          </div>
        </div>
        <div className="gw-howto-step">
          <div className="gw-howto-num">2</div>
          <div className="gw-howto-body">
            <span className="gw-howto-title">Invite friends</span>
            <span className="gw-howto-desc">Invite friends with your own link using <code>/myinvitelink</code></span>
          </div>
        </div>
        <div className="gw-howto-step">
          <div className="gw-howto-num">3</div>
          <div className="gw-howto-body">
            <span className="gw-howto-title">Check spots</span>
            <span className="gw-howto-desc">Run <code>/invites</code> in <strong>#invites</strong></span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CountdownBlock({ label, value, tick }) {
  return (
    <div className="gw-cd-block">
      <div className="gw-cd-num" key={tick ? value : undefined}>
        {String(value).padStart(2, "0")}
      </div>
      <div className="gw-cd-label">{label}</div>
    </div>
  );
}

function PastCard({ giveaway, onViewEntries, index = 0 }) {
  return (
    <div className="gw-past-card" style={{ animationDelay: `${index * 0.07}s` }}>
      <div className="gw-past-head">
        <div className="gw-past-title">{giveaway.title}</div>
        <div className="gw-past-date">
          {new Date(giveaway.end_date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </div>
      </div>
      <div className="gw-past-winners">
        {giveaway.winners.length === 0 && (
          <div className="gw-past-empty">No winners recorded</div>
        )}
        {giveaway.winners.map((w, i) => (
          <div key={i} className="gw-past-winner-row">
            <span className="gw-past-medal">{MEDALS[i] ?? "🏅"}</span>
            <img
              src={`https://mc-heads.net/avatar/${entryName(w)}/40`}
              alt={entryName(w)}
              className="gw-past-avatar"
            />
            <div>
              <div className="gw-past-winner-name">{entryName(w)}</div>
              <div className="gw-past-winner-prize">{w.prize}</div>
            </div>
          </div>
        ))}
      </div>
      {giveaway.entries?.length > 0 && (
        <button className="gw-view-entries-btn" onClick={onViewEntries}>
          View all entries ({giveaway.entries.length})
        </button>
      )}
    </div>
  );
}

export default function GiveawayPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [frozenWinners, setFrozenWinners] = useState(null);
  const [frozenEntries, setFrozenEntries] = useState(null);
  const [spinTriggered, setSpinTriggered] = useState(false);
  const [entriesModal, setEntriesModal] = useState(null);
  const [replayKey, setReplayKey] = useState(0);
  const [isReplay, setIsReplay] = useState(false);

  useEffect(() => {
    let active = true;
    let pickCalled = false;

    const load = async () => {
      const res = await api("getgiveaway");
      if (!active) return;
      if (!res.isSuccess) {
        setError("Failed to load giveaway data.");
        setLoading(false);
        return;
      }
      const d = res.data;
      setData(d);
      setLoading(false);

      // Pick winners 10s after end_date
      if (d.giveaway?.status === "active" && !pickCalled) {
        const msUntilPick = new Date(d.giveaway.end_date) - Date.now() + 10000;
        setTimeout(
          () => {
            if (!active) return;
            pickCalled = true;
            api("pickwinners");
          },
          Math.max(0, msUntilPick),
        );
      }

      // Trigger spin for anyone who hasn't watched it yet
      if (
        d.giveaway?.status === "finished" &&
        d.winners?.length > 0 &&
        !spinTriggered &&
        !hasWatchedSpin(d.giveaway)
      ) {
        setFrozenWinners(d.winners);
        setFrozenEntries(d.entries ?? []);
        setSpinTriggered(true);
      }
    };

    load();
    const iv = setInterval(load, 10000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, [spinTriggered]);

  const timeLeft = useCountdown(data?.giveaway?.end_date);
  const isFinished = data?.giveaway?.status === "finished";
  const isActive = data?.giveaway?.status === "active";
  const isPlaytime = data?.giveaway?.type === "playtime";
  const awaitingWinners = isActive && !timeLeft;
  const showSpin = spinTriggered && frozenWinners?.length > 0;
  const showSummary = !showSpin && isFinished && data?.winners?.length > 0;

  if (loading)
    return (
      <div className="gw-loading">
        <div className="gw-loading-spinner" />
        <span>Loading giveaway…</span>
      </div>
    );
  if (error)
    return <div className="gw-loading">{error}</div>;

  const { giveaway, past } = data;
  const allDiscordTags =
    frozenEntries?.map((e) => entryName(e)).filter(Boolean) ?? [];

  const startReplay = () => {
    setFrozenWinners(data.winners);
    setFrozenEntries(data.entries ?? []);
    setIsReplay(true);
    setSpinTriggered(true);
    setReplayKey((k) => k + 1);
  };

  const placeLabels = { 1: "1st Place", 2: "2nd Place", 3: "3rd Place" };

  return (
    <>
      <div className="gw-hero">
        <video
          className="gw-hero-video"
          src={video}
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="gw-hero-overlay" />
        <div className="gw-hero-sparkles" aria-hidden="true">
          {Array.from({ length: 14 }, (_, i) => (
            <span key={i} style={{ "--i": i }} />
          ))}
        </div>
        <div className="gw-hero-content container">
          <div className="gw-hero-eyebrow">Rewards</div>
          <h1 className="gw-hero-title">
            Give<em>aways</em>
          </h1>
          <p className="gw-hero-sub">
            Win exclusive prizes. Invite friends and earn spots on the wheel.
          </p>
          <div className="gw-hero-chips">
            {isActive && (data.entries?.length ?? 0) > 0 && (
              <span className="gw-hero-chip">
                {data.entries.length} entries
              </span>
            )}
            {isActive && timeLeft && (
              <span className="gw-hero-chip gw-hero-chip--live">
                <span className="gw-live-dot" />
                Live now
              </span>
            )}
            {isFinished && <span className="gw-hero-chip">Giveaway Ended</span>}
          </div>
        </div>
      </div>

      <div className="container section">
        <div className="sec-row">
          <span className="sec-eyebrow sec-eyebrow--left">
            {isFinished ? "Latest Giveaway" : "Active Giveaway"}
          </span>
        </div>

        <div className="gw-current">
          <div className="gw-current-glow" aria-hidden="true" />
          <div className="gw-current-top">
            <div className="gw-current-info">
              <div className="gw-current-title">{giveaway.title}</div>
              {isActive && (
                <p className="gw-current-desc">{giveaway.description}</p>
              )}

              <div className="gw-stat-row">
                {isActive && (data.entries?.length ?? 0) > 0 && (
                  <div className="gw-stat">
                    <span className="gw-stat-num">{data.entries.length}</span>
                    <span className="gw-stat-lbl">people entered</span>
                  </div>
                )}
                {!isPlaytime && giveaway.max_spots && (
                  <div className="gw-stat">
                    <span className="gw-stat-num">{giveaway.max_spots}</span>
                    <span className="gw-stat-lbl">max spots / player</span>
                  </div>
                )}
                {isPlaytime && giveaway.min_playtime_seconds != null && (
                  <div className="gw-stat">
                    <span className="gw-stat-num">
                      {Math.round(giveaway.min_playtime_seconds / 60)} min
                    </span>
                    <span className="gw-stat-lbl">min playtime · equal chance</span>
                  </div>
                )}
              </div>

              {isActive && <HowToEnter giveaway={giveaway} />}
            </div>

            {isActive && (
              <div className="gw-prizes">
                <div className="gw-prizes-label">Prizes</div>
                {giveaway.prizes.map((p, i) => (
                  <div key={i} className={`gw-prize-row gw-prize-row--${p.place ?? i + 1}`}>
                    <span className="gw-prize-medal">
                      {MEDALS[(p.place ?? i + 1) - 1] ?? "🏅"}
                    </span>
                    <div>
                      <div className="gw-prize-label">
                        {placeLabels[p.place] ?? `${p.place}th Place`}
                      </div>
                      <div className="gw-prize-name">{p.prize}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {isActive && timeLeft && (
            <div className="gw-countdown">
              <div className="gw-cd-label-top">Spin happens in</div>
              <div className="gw-cd-blocks">
                <CountdownBlock label="Days" value={timeLeft.d} />
                <span className="gw-cd-sep">:</span>
                <CountdownBlock label="Hours" value={timeLeft.h} />
                <span className="gw-cd-sep">:</span>
                <CountdownBlock label="Minutes" value={timeLeft.m} />
                <span className="gw-cd-sep">:</span>
                <CountdownBlock label="Seconds" value={timeLeft.s} tick />
              </div>
            </div>
          )}

          {isActive && data.entries?.length > 0 && (
            <div className="gw-participants">
              <div className="gw-participants-label">Who's in</div>
              <div className="gw-participants-row">
                {data.entries.slice(0, 8).map((e, i) => (
                  <img
                    key={i}
                    className="gw-participant-avatar"
                    src={`https://mc-heads.net/avatar/${entryName(e)}/32`}
                    alt={entryName(e)}
                    title={entryName(e)}
                    style={{ animationDelay: `${i * 0.05}s` }}
                  />
                ))}
                {data.entries.length > 8 && (
                  <div className="gw-participants-more">
                    +{data.entries.length - 8}
                  </div>
                )}
              </div>
              <button
                className="gw-view-entries-btn-current"
                onClick={() =>
                  setEntriesModal({
                    title: giveaway.title,
                    entries: data.entries,
                    isPlaytime,
                  })
                }
              >
                View all entries ({data.entries.length})
              </button>
            </div>
          )}
        </div>

        {awaitingWinners && (
          <div className="gw-countdown gw-countdown--picking">
            <div className="gw-cd-label-top">Picking winners…</div>
            <div className="gw-picking-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        {showSpin && (
          <SpinSection
            key={replayKey}
            winners={frozenWinners}
            allDiscordTags={allDiscordTags}
            giveaway={giveaway}
            preSpinSeconds={isReplay ? 3 : 10}
            onReplay={startReplay}
          />
        )}
        {showSummary && (
          <WinnersSummary winners={data.winners} onReplay={startReplay} />
        )}

        {past.length > 0 && (
          <>
            <div className="sec-row" style={{ marginTop: "2.75rem" }}>
              <span className="sec-eyebrow sec-eyebrow--left">Previous Giveaways</span>
            </div>
            <div className="grid-auto gw-past-grid">
              {past.map((g, i) => (
                <PastCard
                  key={g.id}
                  giveaway={g}
                  index={i}
                  onViewEntries={() =>
                    setEntriesModal({
                      title: g.title,
                      entries: g.entries ?? [],
                      isPlaytime: false,
                    })
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>

      {entriesModal && (
        <EntriesModal
          title={entriesModal.title}
          entries={entriesModal.entries}
          isPlaytime={entriesModal.isPlaytime ?? false}
          onClose={() => setEntriesModal(null)}
        />
      )}
    </>
  );
}
